/**
 * Legal pages — Terms, Privacy, Acceptable Use.
 *
 * These are required for Stripe checkout, GDPR, and email-footer links.
 * They live as a single file because they share a layout and the content
 * is small enough that splitting helps no one.
 *
 * Source of truth: when policies change, update the document content +
 * bump LAST_UPDATED. Do not maintain a separate CMS for these.
 */
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { ReactNode } from 'react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';

const LAST_UPDATED = 'April 26, 2026';

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <Helmet>
        <title>{title} · InboxGuard</title>
        <meta name="robots" content="index,follow" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <main className="container mx-auto max-w-3xl px-4 pb-20 pt-12 sm:pb-24">
          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
          <div className="prose prose-sm mt-8 max-w-none space-y-6 text-foreground dark:prose-invert">{children}</div>
        </main>
        <PublicFooter />
      </div>
    </>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="mt-8 text-xl font-semibold tracking-tight">{children}</h2>;
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service">
      <p>
        These Terms govern your use of InboxGuard, an email-deliverability monitoring service
        operated by movaMedia, Inc. (&ldquo;movaMedia&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;).
        By creating an account or using the service, you agree to these Terms.
      </p>

      <H2>1. Service description</H2>
      <p>
        InboxGuard performs DNS-based scans of domains you control to evaluate SPF, DKIM, DMARC,
        MTA-STS, TLS-RPT, BIMI, and related deliverability signals. We aggregate DMARC report data
        you forward to us and surface continuous monitoring alerts when configurations drift or
        delivery problems are detected.
      </p>

      <H2>2. Eligibility &amp; account</H2>
      <p>
        You must be 16 or older and authorized to bind the entity creating the account. You are
        responsible for keeping credentials secure and for all activity under your account. You
        agree to provide accurate registration information and to keep it current.
      </p>

      <H2>3. Authorization to scan</H2>
      <p>
        By adding a domain to InboxGuard, you represent that you own it or are authorized to scan
        it. We perform passive DNS lookups, blocklist queries, and policy fetches against the
        public internet — no exploitative or intrusive testing is performed.
      </p>

      <H2>4. Plans &amp; billing</H2>
      <p>
        Paid plans are billed in advance through Stripe on a monthly or annual cycle. Your card is
        charged automatically until you cancel. Cancellation stops future renewals; we do not
        refund the current period unless required by law. Trial subscriptions convert to paid at
        the listed price unless cancelled before the trial ends.
      </p>

      <H2>5. Acceptable use</H2>
      <p>
        Use of InboxGuard is subject to our{' '}
        <Link to="/acceptable-use" className="text-primary underline">
          Acceptable Use Policy
        </Link>
        . Violations may result in immediate suspension.
      </p>

      <H2>6. Data &amp; privacy</H2>
      <p>
        Your use of the service is governed by our{' '}
        <Link to="/privacy" className="text-primary underline">
          Privacy Policy
        </Link>
        . You retain ownership of any data you submit. We process it solely to deliver and improve
        the service.
      </p>

      <H2>7. Service availability</H2>
      <p>
        We aim for high availability but do not promise uptime targets outside of negotiated
        enterprise agreements. Scheduled maintenance, third-party dependencies (DNS, SES, RDS), and
        force-majeure events may cause interruptions.
      </p>

      <H2>8. Termination</H2>
      <p>
        You may delete your account at any time from the Settings page. We may suspend or
        terminate accounts that violate these Terms or the Acceptable Use Policy, fail to pay, or
        engage in conduct harmful to the service or other customers. Upon termination, we delete
        your scan and DMARC report data within 90 days.
      </p>

      <H2>9. Disclaimers</H2>
      <p>
        InboxGuard is provided &ldquo;as is.&rdquo; We do not warrant that scan results, alerts, or
        recommendations are complete, error-free, or fit for a particular purpose. You are
        responsible for evaluating and acting on the information we provide.
      </p>

      <H2>10. Limitation of liability</H2>
      <p>
        To the fullest extent permitted by law, InboxGuard&apos;s aggregate liability for any
        claim arising out of these Terms or the service is limited to the amount you paid us in
        the 12 months preceding the event giving rise to the claim. We are not liable for
        indirect, consequential, or special damages.
      </p>

      <H2>11. Changes</H2>
      <p>
        We may update these Terms by posting a revised version with a new &ldquo;last updated&rdquo;
        date. Material changes will be announced by email at least 14 days before they take effect.
      </p>

      <H2>12. Contact</H2>
      <p>
        Questions about these Terms? Email{' '}
        <a href="mailto:legal@inboxguard.io" className="text-primary underline">
          legal@inboxguard.io
        </a>
        .
      </p>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This Privacy Policy explains how InboxGuard collects, uses, and protects information when
        you use the service. We follow GDPR, CCPA, and standard data-minimization practices.
      </p>

      <H2>1. What we collect</H2>
      <ul className="ml-6 list-disc space-y-1">
        <li>
          <strong>Account data:</strong> email address, organization name, password hash (managed
          by AWS Cognito), and authentication metadata.
        </li>
        <li>
          <strong>Domain &amp; scan data:</strong> domain names you add, public DNS records we
          retrieve, and the resulting scan history.
        </li>
        <li>
          <strong>DMARC reports:</strong> aggregate (RUA) and forensic (RUF) reports you forward
          to InboxGuard mailboxes. Forensic reports may contain message metadata; we redact
          personally identifying content where present.
        </li>
        <li>
          <strong>Postmaster Tools data:</strong> if you connect Google Postmaster Tools, we
          retrieve reputation, spam-rate, and authentication metrics for the domains you authorize.
        </li>
        <li>
          <strong>Billing data:</strong> handled directly by Stripe. We store a Stripe customer
          ID, plan, and subscription state — never card numbers or full account details.
        </li>
        <li>
          <strong>Technical logs:</strong> request metadata, IP, user-agent, and error traces for
          security and debugging. Retained for up to 30 days.
        </li>
      </ul>

      <H2>2. How we use it</H2>
      <p>
        We use the data to operate the service: run scans, deliver alerts, generate reports,
        process payments, and provide support. Aggregate, non-identifying metrics may inform
        product improvements. We never sell your data.
      </p>

      <H2>3. Sharing</H2>
      <p>
        We share data only with vetted subprocessors: AWS (hosting, email delivery, authentication),
        Stripe (payments), and Google (Postmaster Tools, if you connect it). A current list is
        available on request.
      </p>

      <H2>4. Where data is stored</H2>
      <p>
        Production data is stored in AWS us-east-1 (United States). Personal data of EU/UK data
        subjects transferred to the United States is protected under the EU Standard Contractual
        Clauses and the UK International Data Transfer Addendum (see our DPA).
      </p>

      <H2>5. Retention</H2>
      <p>
        Active-account data is retained while your account is active. Scan history is kept for the
        life of your account. After deletion, we remove personal data within 90 days; aggregate,
        anonymized statistics may be retained indefinitely. Backup tapes age out within 35 days.
      </p>

      <H2>6. Your rights</H2>
      <p>
        You can access, correct, export, or delete your data at any time. EU/UK residents have GDPR
        rights to portability, restriction, and objection; California residents have equivalent
        CCPA rights. To exercise any of these, email{' '}
        <a href="mailto:privacy@inboxguard.io" className="text-primary underline">
          privacy@inboxguard.io
        </a>
        .
      </p>

      <H2>7. Cookies</H2>
      <p>
        We use first-party cookies for authentication only. We do not use advertising or
        third-party tracking cookies on the application surface. Marketing pages may include a
        privacy-respecting analytics cookie that you can opt out of.
      </p>

      <H2>8. Security</H2>
      <p>
        Data in transit is encrypted (TLS 1.2+); data at rest is encrypted with AWS-managed keys.
        Access to production systems is gated by IAM with MFA, least-privilege roles, and audit
        logs. We run quarterly security reviews and respond to vulnerability reports at{' '}
        <a href="mailto:security@inboxguard.io" className="text-primary underline">
          security@inboxguard.io
        </a>
        .
      </p>

      <H2>9. Children</H2>
      <p>
        InboxGuard is not directed at children under 16 and we do not knowingly collect data from
        them. If you believe we have, contact us and we will delete it.
      </p>

      <H2>10. Changes</H2>
      <p>
        Material changes are announced by email at least 14 days before they take effect. The
        &ldquo;last updated&rdquo; date at the top reflects the most recent revision.
      </p>

      <H2>11. Contact</H2>
      <p>
        Email{' '}
        <a href="mailto:privacy@inboxguard.io" className="text-primary underline">
          privacy@inboxguard.io
        </a>{' '}
        with any privacy question or request.
      </p>
    </LegalLayout>
  );
}

export function AcceptableUsePage() {
  return (
    <LegalLayout title="Acceptable Use Policy">
      <p>
        This Acceptable Use Policy applies to anyone using InboxGuard. Violations may result in
        suspension or termination without refund.
      </p>

      <H2>Permitted use</H2>
      <p>
        InboxGuard is a tool for monitoring the deliverability and authentication posture of
        domains you control or are authorized to scan. Permitted activities include adding your
        own domains, forwarding DMARC reports for those domains, connecting your own Postmaster
        Tools account, and sharing scan results with internal or external stakeholders.
      </p>

      <H2>Prohibited use</H2>
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Scanning or claiming domains you do not own or are not authorized to scan, including
          competitors, customers, or third parties without their consent.
        </li>
        <li>
          Using InboxGuard data for spamming, phishing, fraud, or any other deceptive practice.
        </li>
        <li>
          Reverse-engineering, scraping, or automated bulk-querying the service in ways that
          exceed reasonable rate limits or your plan&apos;s entitlements.
        </li>
        <li>
          Attempting to access, probe, or test the vulnerability of any system or network without
          authorization. Vulnerability research within scope of our security policy is welcome —
          email security@inboxguard.io before testing.
        </li>
        <li>
          Sharing account credentials. Each user must have their own login.
        </li>
        <li>
          Reselling or white-labeling InboxGuard outside of an MSP/Agency plan.
        </li>
      </ul>

      <H2>Reporting abuse</H2>
      <p>
        Suspected abuse should be reported to{' '}
        <a href="mailto:abuse@inboxguard.io" className="text-primary underline">
          abuse@inboxguard.io
        </a>{' '}
        with as much detail as possible. We investigate every report.
      </p>

      <H2>Enforcement</H2>
      <p>
        We may suspend access without notice for clear or imminent violations. For ambiguous cases,
        we will reach out to you first. Termination is final once decided; appeals can be sent to
        legal@inboxguard.io.
      </p>
    </LegalLayout>
  );
}

export function DpaPage() {
  return (
    <LegalLayout title="Data Processing Agreement">
      <p>
        This Data Processing Agreement (&ldquo;DPA&rdquo;) supplements the Terms of Service between
        you (&ldquo;Customer&rdquo; or &ldquo;Controller&rdquo;) and movaMedia, Inc., the operator of
        InboxGuard (&ldquo;Processor&rdquo;). It applies whenever InboxGuard processes personal data on behalf
        of Customer in the course of providing the service. For EU/UK customers, this DPA
        incorporates the EU Standard Contractual Clauses (Module Two) by reference.
      </p>

      <H2>1. Roles &amp; scope</H2>
      <p>
        Customer is the Controller of personal data submitted to the service. InboxGuard acts as
        Processor and processes that data only to provide the service, comply with documented
        Customer instructions, and meet legal obligations. The subject matter is email-deliverability
        monitoring; the duration is the term of the subscription plus any retention period below.
      </p>

      <H2>2. Categories of data &amp; data subjects</H2>
      <ul className="ml-6 list-disc space-y-1">
        <li>
          <strong>Account &amp; identity data</strong> — names, email addresses, role, organization;
          subjects are Customer&apos;s users and admins.
        </li>
        <li>
          <strong>DMARC report metadata</strong> — sender IPs, header-from, authentication results,
          message counts; subjects are senders to/from Customer&apos;s domains.
        </li>
        <li>
          <strong>Postmaster Tools metrics</strong> — aggregate reputation and authentication data
          from Google for domains Customer authorizes.
        </li>
        <li>
          <strong>Operational logs</strong> — request metadata, IP, user-agent, error traces.
        </li>
      </ul>

      <H2>3. Customer instructions</H2>
      <p>
        InboxGuard processes personal data only on Customer&apos;s documented instructions, including
        configuration and use of the service. InboxGuard will notify Customer if, in its opinion, an
        instruction violates applicable data-protection law.
      </p>

      <H2>4. Subprocessors</H2>
      <p>
        Customer authorizes InboxGuard to engage the following subprocessors: Amazon Web Services
        (hosting, storage, email delivery, authentication — United States), Stripe (payments — US),
        and Google LLC (Postmaster Tools — only when Customer connects it). InboxGuard will give
        Customer at least 30 days&apos; notice of new subprocessors via email or product notice;
        Customer may object on reasonable grounds.
      </p>

      <H2>5. Security measures</H2>
      <p>
        InboxGuard implements technical and organizational measures appropriate to the risk,
        including: TLS 1.2+ for data in transit, AES-256 (AWS-managed keys) for data at rest, KMS
        envelope encryption for OAuth refresh tokens, IAM least-privilege with MFA, separation of
        duties between staging and production, audit logs (CloudTrail, CloudWatch), DLQ-backed
        retries with poison-pill detection, and quarterly security reviews. Annex II of this DPA
        details the current measures and is updated as practices evolve.
      </p>

      <H2>6. International transfers</H2>
      <p>
        For EU/UK personal data transferred outside the EEA/UK, the parties incorporate the EU
        Standard Contractual Clauses (Commission Implementing Decision (EU) 2021/914) Module Two
        (Controller-to-Processor) and the UK International Data Transfer Addendum. Production data is
        hosted in the United States (AWS us-east-1).
      </p>

      <H2>7. Data subject rights</H2>
      <p>
        Where a data subject contacts InboxGuard directly, InboxGuard will not respond to the
        substance and will forward the request to Customer without undue delay. InboxGuard provides
        the tools necessary for Customer to fulfill access, correction, deletion, and portability
        requests through the application or via support@inboxguard.io.
      </p>

      <H2>8. Personal-data breach</H2>
      <p>
        InboxGuard will notify Customer without undue delay (and in any event within 72 hours of
        becoming aware) of any confirmed personal-data breach affecting Customer&apos;s data. The
        notice will describe the nature of the breach, the categories and approximate number of
        affected data subjects, the likely consequences, and the measures taken or proposed.
      </p>

      <H2>9. Audits</H2>
      <p>
        Once per twelve-month period (more often where required by a regulator or following a
        confirmed breach), Customer may request — and InboxGuard will provide — reasonable written
        responses to a security questionnaire and any third-party attestations InboxGuard then holds.
        On-site audits are available to enterprise customers under a mutual NDA at the customer&apos;s
        expense.
      </p>

      <H2>10. Return &amp; deletion</H2>
      <p>
        On termination, InboxGuard will, at Customer&apos;s choice, delete or return all personal
        data and delete existing copies, unless retention is required by law. Deletion is completed
        within 90 days; backups age out within 35 days thereafter.
      </p>

      <H2>11. Liability</H2>
      <p>
        Each party&apos;s liability under this DPA is subject to the limitations of liability in the
        Terms of Service.
      </p>

      <H2>12. Contact</H2>
      <p>
        For DPA-specific requests — including signed copies, subprocessor objections, or audit
        questionnaires — email{' '}
        <a href="mailto:privacy@inboxguard.io" className="text-primary underline">
          privacy@inboxguard.io
        </a>
        .
      </p>
    </LegalLayout>
  );
}

export function CookiePage() {
  return (
    <LegalLayout title="Cookie Policy">
      <p>
        This Cookie Policy explains how InboxGuard uses cookies and similar storage technologies on
        our marketing pages and inside the application. We use the minimum necessary to make the
        service work and to understand product usage in aggregate. We do not sell cookie data, and
        we do not run third-party advertising.
      </p>

      <H2>1. What are cookies?</H2>
      <p>
        Cookies are small text files stored by your browser. &ldquo;First-party&rdquo; cookies come
        from inboxguard.io and are read only by us. We may also use{' '}
        <code>localStorage</code> and <code>sessionStorage</code> for the same purposes; this Policy
        covers both.
      </p>

      <H2>2. Cookies we set</H2>
      <ul className="ml-6 list-disc space-y-1">
        <li>
          <strong>Authentication (strictly necessary).</strong> AWS Cognito sets short-lived ID and
          refresh tokens in browser storage so you stay signed in. Without these the app cannot
          function. No expiry beyond your sign-in session.
        </li>
        <li>
          <strong>CSRF / request integrity (strictly necessary).</strong> A first-party token is
          stored to protect form submissions. Cleared on sign-out.
        </li>
        <li>
          <strong>Preferences (functional).</strong> Theme, sidebar collapse state, and which org is
          currently active for users in multiple orgs. Stored locally; never sent to third parties.
        </li>
        <li>
          <strong>Analytics (optional).</strong> Aggregate, IP-truncated product analytics on
          marketing pages only — measures things like &ldquo;how many people visit /pricing.&rdquo;
          Not used inside the authenticated app. Honors browser <code>Do Not Track</code> and the{' '}
          <code>Sec-GPC</code> Global Privacy Control header.
        </li>
      </ul>

      <H2>3. What we do NOT use</H2>
      <p>
        We do not use third-party advertising cookies, retargeting pixels, social-media trackers, or
        cross-site fingerprinting. The authenticated app surface uses zero non-essential cookies.
      </p>

      <H2>4. Managing cookies</H2>
      <p>
        Most browsers let you block or delete cookies via Settings. Blocking strictly necessary
        cookies will prevent sign-in. EU/UK visitors are shown a consent banner on first visit;
        declining keeps only strictly necessary cookies.
      </p>

      <H2>5. Changes</H2>
      <p>
        Material changes are announced by email and reflected in the &ldquo;last updated&rdquo;
        date. The current cookie inventory is available on request to{' '}
        <a href="mailto:privacy@inboxguard.io" className="text-primary underline">
          privacy@inboxguard.io
        </a>
        .
      </p>
    </LegalLayout>
  );
}
